import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { ClientForm } from '@/apps/coach/components/admin/ClientForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ClientFile } from '@/types/coach';

export function CoachDashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientFile[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abandoned = false;
    api.coach.me()
      .then((me) => {
        if (!me.isCoach) {
          if (!abandoned) navigate(routes.coach.welcome(), { replace: true });
          return null;
        }
        return api.coach.listClients();
      })
      .then((list) => {
        if (abandoned || !list) return;
        setClients(list);
      })
      .catch((err) => {
        if (abandoned) return;
        const msg = err instanceof Error ? err.message : '';
        if (msg.startsWith('API 401')) {
          navigate(routes.coach.welcome(), { replace: true });
          return;
        }
        setError(msg || 'Fehler beim Laden.');
      });
    return () => { abandoned = true; };
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const q = search.trim().toLowerCase();
    return clients
      .filter((c) => (showArchived ? true : !c.archived))
      .filter((c) => {
        if (!q) return true;
        const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
        return fullName.includes(q);
      });
  }, [clients, search, showArchived]);

  function handleCreated(saved: ClientFile) {
    setClients((prev) => (prev ? [saved, ...prev] : [saved]));
    toast.info('Klient angelegt.');
  }

  return (
    <AdminShell pageTitle="Klienten">
      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Klienten</h1>
            <p className="text-sm text-muted-foreground">
              Deine Klient-Akten und Sessions.
            </p>
          </div>
          <Button onClick={() => setFormOpen(true)} className="gap-1.5">
            <Plus size={16} />
            Neuer Klient
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {clients === null ? (
          <p className="text-sm text-muted-foreground">Lade…</p>
        ) : clients.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-10 text-center">
            <div className="inline-flex w-12 h-12 rounded-full bg-muted items-center justify-center mb-3">
              <Users size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">Noch keine Klienten</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              Leg deinen ersten Klient an, um loszulegen.
            </p>
            <Button onClick={() => setFormOpen(true)} className="gap-1.5">
              <Plus size={16} />
              Klient anlegen
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Klient suchen..."
                className="flex-1"
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground select-none px-3">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                Archivierte anzeigen
              </label>
            </div>

            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium hidden md:table-cell">
                      Hinzugefügt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={routes.coach.client(c.id)}
                          className={`block font-medium ${
                            c.archived ? 'text-muted-foreground' : ''
                          }`}
                        >
                          {c.firstName} {c.lastName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {c.archived ? (
                          <span className="text-muted-foreground">Archiviert</span>
                        ) : (
                          <span className="text-green-600">● Aktiv</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">
                        {new Date(c.createdAt).toLocaleDateString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Keine Klienten passen zur Suche.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <ClientForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={handleCreated}
      />
    </AdminShell>
  );
}
